import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactTooltip from 'react-tooltip'
import { useSelector, useDispatch } from 'react-redux'
import { Autocomplete, Divider, MenuItem, IconButton, TextField } from '@mui/material'
import { ArrowBack, Search } from '@mui/icons-material'
import { CmpStr } from 'cmpstr'
import _ from 'lodash'

import { enabledFeatures } from 'config/env'
import backend from 'utils/backend'
import { useDebounce, useOutsideRef } from 'hooks'
import { selectFacets } from 'store/actions/facets'
import { changeSearchText, searchFundersByParams } from 'store/actions/search'
import { serializeSearchParams } from 'components/SearchParams/helpers'
import { selectFlatSearchFacets } from 'store/selectors/facets'
import { selectSearchText } from 'store/selectors/search'
import { resetFacetFilters } from 'store/actions/filters'
import {
  trackSearchAutocompleteFacetClick,
  trackSearchAutocompleteFunderClick
} from 'utils/mixpanel'

const cmpstr = CmpStr.create({ metric: 'dice', flags: 'di' })

const compareSearchText = (searchText, item) => {
  const fullScore = cmpstr.compare(searchText, item.title)
  const batchScores = cmpstr.batchTest(searchText.split(' '), item.title.split(' '))
  const wordScoresMap = batchScores.reduce(
    (acc, { target, match }) => ({
      ...acc,
      [target]: Math.max(acc[target] || 0, match)
    }),
    {}
  )
  const wordScores = Object.values(wordScoresMap)
  const wordScoreAvg = wordScores.reduce((acc, score) => acc + score, 0) / wordScores.length
  return Math.max(fullScore, Math.max(...wordScores) * 0.7 + wordScoreAvg * 0.3)
}

const filterFacetsBySearchText = (searchText, facets) => {
  if (enabledFeatures.includes('SEARCH_FORM_FACET_FUZZY_MATCHING')) {
    return facets
      .map(item => ({ ...item, score: compareSearchText(searchText, item) }))
      .filter(item => item.score > 0.5)
      .sort((a, b) => b.score - a.score)
  } else {
    return facets.filter(item => item.title.toLowerCase().includes(searchText.toLowerCase()))
  }
}

const SearchForm = ({ gotoFunder, mobile = false }) => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)
  const searchText = useSelector(selectSearchText)
  const flatSearchFacets = useSelector(selectFlatSearchFacets)

  const [showSearchForm, setShowSearchForm] = useState(false)
  const [localSearchText, setLocalSearchText] = useState(searchText || '')
  const debouncedSearchTerm = useDebounce(localSearchText, 500)
  const [filteredFacets, setFilteredFacets] = useState([])
  const [localResults, setLocalResults] = useState([])

  const autocompleteStyle = mobile
    ? 'Header-search-mobile-field-container'
    : 'Header-search-field-container'

  const inputRef = useRef(null)

  const [formHighlighted, setFormHighlighted, formRef] = useOutsideRef()

  useEffect(() => setLocalSearchText(searchText || ''), [searchText])

  useEffect(() => {
    const getQuickResults = async searchText => {
      const searchParams = serializeSearchParams({
        searchText,
        pageNumber: 1,
        viewsPerPage: 5,
        sortBy: 'match',
        searchFields: ['name', 'alias']
      })
      const { data: { results } = {} } = await backend.get(`api/search/funders/${searchParams}`)
      setLocalResults(
        results.map(item => ({
          key: `funder-${item.id}`,
          text: item.alias ? `${item.name} (${item.alias})` : item.name,
          value: { funder: item.id }
        }))
      )
    }

    const getFilteredFacets = searchText => {
      setFilteredFacets(
        filterFacetsBySearchText(searchText, flatSearchFacets)
          .slice(0, 5)
          .map(item => ({
            key: `facet-${item.facetType}-${item.id}`,
            text: `${item.title} ${t.global.in} ${_.get(t, item.parent)}`,
            value: {
              facet: item.id,
              facetType: item.facetType,
              parentFacetIds: item.parentFacetIds
            }
          }))
      )
    }

    if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
      getFilteredFacets(debouncedSearchTerm)
      getQuickResults(debouncedSearchTerm)
    } else {
      setFilteredFacets([])
      setLocalResults([])
    }
  }, [debouncedSearchTerm, flatSearchFacets])

  const handleFormSubmit = () => {
    // Needed to hide the autocomplete popover when clicking on the keyword search button
    inputRef.current.blur()
    setFormHighlighted(false)

    // full text search
    dispatch(changeSearchText(localSearchText))
    dispatch(resetFacetFilters())
    dispatch(searchFundersByParams('SEARCH_FORM'))

    setLocalResults([])
    setFilteredFacets([])
  }

  const handleInputChange = (event, value) => {
    setLocalSearchText(value)
  }

  const handleNewRequest = async (event, item) => {
    if (!_.has(item, 'value')) {
      handleFormSubmit()
      return
    }

    if (_.has(item.value, 'funder')) {
      gotoFunder(item.value.funder)
      await trackSearchAutocompleteFunderClick(item.value.funder)
    } else if (_.has(item.value, 'facet')) {
      dispatch(changeSearchText(''))
      dispatch(resetFacetFilters())
      dispatch(selectFacets([{ id: item.value.facet, facetType: item.value.facetType }]))
      dispatch(searchFundersByParams())
      await trackSearchAutocompleteFacetClick({
        id: item.value.facet,
        facetType: item.value.facetType
      })
    }

    setLocalResults([])
    setFilteredFacets([])
  }

  return (
    <div>
      {((showSearchForm && mobile) || !mobile) && (
        <form className="Header-search" ref={formRef}>
          {mobile && (
            <IconButton onClick={e => setShowSearchForm(false)}>
              <ArrowBack />
            </IconButton>
          )}
          {/* Autocomplete Field */}
          <div className={autocompleteStyle}>
            <Autocomplete
              data-tip={t.search.toolTip}
              fullWidth
              freeSolo
              disableClearable
              blurOnSelect
              className={`Header-search__autocomplete ${formHighlighted}`}
              slotProps={{ popper: { className: 'Header-search__dropdown' } }}
              openOnFocus={true}
              options={
                ((localResults.length || filteredFacets.length) && [
                  ...filteredFacets,
                  ...(filteredFacets.length && localResults.length
                    ? [{ key: 'divider', text: 'divider', elem: <Divider /> }]
                    : []),
                  ...localResults,
                  {
                    key: 'extra',
                    text: localSearchText,
                    elem: (
                      <MenuItem onClick={handleFormSubmit} className="extra">
                        <div className="extra--text">
                          <div>{t.search.extraMenuItem}</div>
                          <div>{t.search.extraMenuItemSecond}</div>
                        </div>
                        <div className="extra--item">{t.search.go}</div>
                      </MenuItem>
                    )
                  }
                ]) ||
                []
              }
              getOptionLabel={opt => opt.text || ''}
              getOptionKey={opt => opt.key || ''}
              renderOption={({ key, ...props }, opt) => (
                <React.Fragment key={key}>
                  {opt.elem || <MenuItem {...props}>{opt.text}</MenuItem>}
                </React.Fragment>
              )}
              onFocus={() => setFormHighlighted('highlight')}
              filterOptions={key => key}
              inputValue={localSearchText}
              onInputChange={handleInputChange}
              onChange={handleNewRequest}
              renderInput={params => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  variant="standard"
                  placeholder={t.search.hintText}
                />
              )}
            />
            <ReactTooltip place="bottom" effect="solid" />
          </div>

          <IconButton
            id="Header-submit"
            className="Header-search__submit"
            onClick={handleFormSubmit}
          >
            <Search />
          </IconButton>
        </form>
      )}
      {!showSearchForm && mobile && (
        <IconButton className="Header-search__open-search" onClick={e => setShowSearchForm(true)}>
          <Search />
        </IconButton>
      )}
    </div>
  )
}

export default SearchForm
